import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "commons/store/store";
import axiosSecureInstance from "commons/axios/axiosSecureInstance";
import { HalResource } from "types/halResource.types";
import { PurpleItem, StatsItem } from "../components/DashboardStats/StatsTypes";

export interface SurveyState {
    surveySegments: any; // todo model
    stats: { item: StatsItem[]; researchStartedAt?: string; totalItems: number; };
    statsSurveys: HalResource<any>;
    surveys: {
        itemsPerPage: number;
        totalItems: number;
        data: any[];
    };
    isLoading: boolean;
    error: any;
}

const initialState: SurveyState = {
    surveys: {
        itemsPerPage: 0,
        totalItems: 0,
        data: [],
    },
    surveySegments: [],
    stats: { item: [], researchStartedAt: undefined, totalItems: 0 },
    statsSurveys: {
      _links: {},
      itemsPerPage: 0,
      totalItems: 0
    },
    isLoading: false,
    error: null
};

type fetchSurveysParams = {
    page: number;
};

export const fetchSurveys = createAsyncThunk(
    "dashboard/fetchSurveys",
    async ({page}: fetchSurveysParams) => {
        const response = await axiosSecureInstance.get<HalResource<any[]>>("/api/surveys", {
            headers: {
                accept: "application/hal+json",
            },
            params: {
                pagination: true,
                page,
                itemsPerPage: 30
            },
        });
        return response.data;
    }
);

export const fetchSurveySegments = createAsyncThunk(
  "dashboard/fetchSurveySegments", async (id: string) => {
    const response = await axiosSecureInstance.get<any>(
        `/api/surveys/${id}/segments`
    );
    return response.data;
});

export const fetchStats = createAsyncThunk(
  "dashboard/fetchStats", async () => {
    const response = await axiosSecureInstance.get<any>(`/api/stats`);
    return response.data;
});

export const fetchStatsSurveys = createAsyncThunk(
  "dashboard/fetchStatsSurveys",
  async (config: any, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.get<HalResource<any>>(`/api/surveys/statistics`, config);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const addSurvey = createAsyncThunk("users/addSurvey", async () => {
    try {
        const response =
            await axiosSecureInstance.post<any>(
                `/api/surveys`,
                {}
            );
        return response.data;
    } catch (error: any) {
        const {response} = error;
        const {request, ...errorObject} = response; // take everything but 'request'
        alert(errorObject?.data?.detail)
        return errorObject;
    }

});

export const exportFile = createAsyncThunk(
  "dashboard/exportFile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/surveys/statistics/export/xlsx`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['Content-Disposition'] || response.headers['contentDisposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `survey_statistics_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match.length > 1) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
        resetSurveySegments(state) {
            state.surveySegments = initialState.surveySegments;
        },
    },
    extraReducers: (builder) => {
        // builder.addCase(fetchSurveys.fulfilled, (state, action) => {
        //   state.surveys.data = action.payload._embedded?.item || [];
        //   state.surveys.itemsPerPage = action.payload.itemsPerPage;
        //   state.surveys.totalItems = action.payload.totalItems;
        // });
        builder.addCase(fetchSurveySegments.fulfilled, (state: SurveyState, action) => {
            state.surveySegments = action.payload;
        });
        builder.addCase(fetchStats.fulfilled, (state: SurveyState, action) => {
            state.stats = action.payload;
        });
        builder.addCase(fetchStatsSurveys.fulfilled, (state: SurveyState, action) => {
            state.statsSurveys = action.payload;
        });
        builder
          .addCase(exportFile.pending, (state) => {
            state.isLoading = true;
            state.error = null;
          })
          .addCase(exportFile.fulfilled, (state) => {
            state.isLoading = false;
          })
          .addCase(exportFile.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
          });
    },
});

export const selectSurveys = (state: RootState) => state.dashboard.dashboard.surveys;
export const selectSurveySegments = (state: RootState) => state.dashboard.dashboard.surveySegments;
export const selectStats = (state: RootState) => state.dashboard.dashboard.stats;
export const selectStatsSurveys = (state: RootState) => state.dashboard.dashboard.statsSurveys;
export const selectSurveyResourceLoading = (state: RootState) => state.dashboard.dashboard.isLoading;

export const { resetSurveySegments } = dashboardSlice.actions;

export default dashboardSlice.reducer;
